import React, { Fragment, useState } from "react";
import Axios from "axios";
import Form from "react-bootstrap/Form";
import { useNavigate, Link } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import moveImage from "../assets/images/move.jpg";
// Contexts
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import LanguageSwitcher from "./common/languageSwitcher";
// Stores
import useUserStore from "../stores/useUserStore";
// Utils
import { getApiErrorMessage } from "../utils/apiMessageHelper";

export const Login = () => {
  console.log("Environment Variables:", process.env);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // For password visibility
  // Contexts
  const { login } = useAuth();
  const { notify } = useNotification();
  // Stores
  const { setUser } = useUserStore();

  function validateForm() {
    return userName.length > 0 && password.length > 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/Auth/login`,
        { user: userName, password: password },
      );

      console.log("login", res.data);
      const { role, token, idClient, isTemp, idUser, image } =
        res.data.responseObject;
      const permissions = role.permissions.map((value) => value.name);

      Axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      sessionStorage.setItem("token", token);
      sessionStorage.setItem("permissions", JSON.stringify(permissions)); // Save the permissions in the session storage
      sessionStorage.setItem("idUser", idUser); // Save the idUser in the session storage
      sessionStorage.setItem("userEmail", userName); // Save the user email in the session storage

      // Guardar datos del usuario en el store de Zustand
      setUser({
        email: userName,
        image: image || null,
        idUser: idUser,
        clientId: idClient,
      });

      // Call the login function from the context
      login(token, permissions, idClient);

      if (isTemp) {
        notify(t("auth.login.tempPasswordWarning"), { type: "warning" });
        navigate(`/reset-password/${userName}`);
        return;
      }

      switch (role.name) {
        case "Admin Girs":
        case "Creador de Rutinas":
          navigate(role.url);
          break;

        case "Usuario APP":
          navigate("/move");
          break;

        case "Administrador General":
          sessionStorage.setItem("clientId", idClient);
          navigate("/dashboard-client");
          break;

        default:
          if (role.name !== "") {
            sessionStorage.setItem("clientId", idClient);
            navigate(role.url + `${idClient}`);
          } else {
            notify(t("auth.login.unauthorizedWarning"), { type: "warning" });
          }
          break;
      }
    } catch (err) {
      // Use the new API message helper for automatic translation
      const errorMessage = getApiErrorMessage(err);
      notify(errorMessage, { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Fragment>
      <div className="auth-wrapper">
        <div className="auth-content subscribe">
          <div className="card">
            <div className="row no-gutters">
              <div
                className="col-md-4 col-lg-6 d-none d-md-flex d-lg-flex align-items-center justify-content-center"
                style={{ backgroundColor: "#24bdc1" }}
              >
                <img src={moveImage} alt="Move logo" className="img-fluid" />
              </div>
              <div className="col-md-8 col-lg-6">
                <div className="card-body text-center">
                  <div className="row justify-content-center">
                    <div className="col-sm-10">
                      <Form onSubmit={handleSubmit}>
                        <h3 className="mb-4">{t("auth.login.pageTitle")}</h3>
                        <div className="input-group mb-3">
                          <input
                            type="email"
                            className="form-control"
                            placeholder={t("auth.login.emailPlaceholder")}
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            disabled={loading}
                          />
                        </div>
                        <div className="input-group mb-4 position-relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            placeholder={t("auth.login.passwordPlaceholder")}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                          />
                          <span
                            className="position-absolute end-0 top-50 translate-middle-y me-3"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: "pointer" }}
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                              size="lg"
                              color="#6c757d"
                            />
                          </span>
                        </div>
                        <button
                          type="submit"
                          className="btn btn-primary shadow-2 mb-4"
                          disabled={!validateForm() || loading}
                        >
                          {loading ? (
                            <ClipLoader size={20} color={"#ffffff"} />
                          ) : (
                            t("auth.login.submitButton")
                          )}
                        </button>
                        <p className="mb-2 text-muted">
                          <Link to="/forgot-password">
                            {t("auth.login.forgotPasswordLink")}
                          </Link>
                        </p>
                      </Form>
                      <div className="mt-3 d-flex justify-content-center">
                        <LanguageSwitcher variant="light" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
